import { AppNS } from 'app';
import { chunk } from 'lodash-es';

import { parseAgencyDocumentsResponse, parseSingleDocumentData } from '../agency-documents/agency-documents-parsers';

export const parseAvatar = (avatarParams: AppNS.Params): string => {
  if (avatarParams) {
    return avatarParams.versions.small;
  }
  return '';
};

export const parseJobData = (data: AppNS.Params): AppNS.Project => {
  const permits = data.permits ? parsePermitsForJob(data.permits) : [];
  return {
    id: data.id,
    projectType: data.whenType,
    addressIds: data.addressIds,
    startDate: data.startDate,
    endDate: data.endDate,
    agency: data.agency,
    agencyCompany: data.agencyCompany,
    client: data.client,
    contactEmail: data.siteContactEmail,
    contactName: data.siteContactName,
    contactPhone: data.siteContactPhone,
    description: data.additionalInfo,
    address: {
      quadrant: data.quadrant,
      streetName: data.streetName,
      streetNumber: data.streetNo,
      streetNumberSuffix: data.streetNoSuffix,
      streetType: data.streetType,
      unitNumber: data.streetUnitNo,
      zip: data.zip,
    },
    permitNumbers: data.permitNumbers,
    inspectionTypes: data.inspectionTypes,
    pickedPermits: permits.map((item) => {
      return {
        id: item.id,
        isOn: true,
        inspections: item.inspections
          ? item.inspections?.map((insp) => insp.id)
          : [],
        inspectionGroup: item.inspectionGroup || '',
        addressId: item.addressId || '',
        cluster: item.cluster,
        wallCheckComplete: item.wallCheckComplete,
        wallCheckRequired: item.wallCheckRequired,
      };
    }),
    permits,
    eventReports: data.eventReports ? data.eventReports : [],
    jobChanges: parseChangesForJob(data.jobChanges),
    jobDocuments: parseDocumentsForJob(data.jobDocuments),
    state: data.state,
    cancellationMessage: data.cancellationMessage,
    links: data.links,
    type: data.type,
    scope: data.scope,
    scopes: data.scopes,
    paymentMethodId: data?.paymentMethodId || '',
    // front meta data
    dbAddress: null,
    owner: '',
    searchBy: '',
    permitId: '',
    legacyProject: data.legacyProject,
    legacyProjectTpia: data.legacyProjectTpia,
    createdAt: data.createdAt,
  };
};

export const parseChangesForJob = (
  data: Array<AppNS.Params>
): Array<AppNS.JobChange> => {
  if (!data) return [];

  return data.map((item) => {
    return {
      ...item,
      permit: parsePermitsForJob([item.permit])[0],
    } as unknown as AppNS.JobChange;
  });
};

export const parseDocumentsForJob = (
  data: Array<AppNS.Params>
): Array<AppNS.JobDocument> => {
  if (!data) return [];

  return data.map((item) => {
    return {
      id: item.id,
      permitIds: item.permitIds,
      permitNumbers: item.permitNumbers,
      dcraState: item.dcraState,
      type: item.type,
      name: item.name,
      fileUrl: item.fileUrl,
      dcraStateRejectionComment: item.dcraStateRejectionComment,
      document: item.document && parseSingleDocumentData(item.document),
    } as unknown as AppNS.JobDocument;
  });
};

export const parsePermitsForJob = (
  permits: Array<AppNS.Params>
): Array<AppNS.Permit> => {
  return permits.map((item) => {
    return {
      id: item.id,
      name: '',
      owner: '',
      inspections: item.permitInspections.map((insp: AppNS.Params) => {
        return {
          id: insp.id || insp.inspectionType,
          status: insp.state,
          name: insp.inspectionType,
        };
      }),
      permitNumber: item.permitNumber,
      scope: item.scope,
      state: item.state,
      inspectionGroup: item.inspectionGroup || '',
      wallCheckRequired: item.wallCheckRequired,
      wallCheckComplete: item.wallCheckComplete,
    } as AppNS.Permit;
  });
};

export const parseLeadProposal = (
  data: AppNS.Params
): AppNS.LeadProposal | null => {
  if (!data || !data.estimatedBudget) {
    return null;
  }
  return {
    id: data.id,
    terms: data.termsAndConditions,
    visits: data.numberOfVisits ? parseVisits(data.numberOfVisits) : [],
    budget: data.estimatedBudget,
    isSend: true,
    services: data.description,
    pricingPolicy: data.detailsAboutPricing,
    paymentArrangement: data.paymentArrangement,
    files: data.documents ? parseAgencyDocumentsResponse(data.documents) : [],
  } as AppNS.LeadProposal;
};

export const parseVisits = (
  data: Array<string>
): Array<AppNS.LeadProposalVisit> => {
  const chunkedData = chunk(data, 2);
  return chunkedData.map((item: Array<string>) => {
    return {
      id: item[0],
      visits: item[1],
      permitNumber: item[0],
    } as AppNS.LeadProposalVisit;
  });
};

export const parseReportInspections = (
  data: Array<AppNS.Params>
): Array<AppNS.Inspection> => {
  return data.map((item: AppNS.Params) => {
    return {
      ...item,
      status: item.state,
      name: item.inspectionType,
      files: parseAgencyDocumentsResponse(item.documents),
    } as AppNS.Inspection;
  });
};
